import { toNumber } from '@/utils/dataUtils';
import type { VehicleLoanRow, VehicleLoanSum } from '@/services/auditService/interface/wp-row';
import {
    effectiveLoanSum,
    resolveBalanceYoa,
    resolveNonCurrentLastYoa,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanSummarySums';
import { resolveBankLoanLastYoaTotal } from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanSumCalc';

function hasDefinedValue(v: unknown): boolean {
    return v !== undefined && v !== null;
}

/**
 * Resolve the effective value shown in VehicleLoanDetail for a sum field.
 * - xxx 有值且 xxxChange 有值 → 使用 xxxChange
 * - xxx 有值且 xxxChange 无值或为 null → 使用 xxx
 */
export function resolveVehicleLoanDisplayValue(
    sums: Partial<VehicleLoanSum> | undefined,
    field: keyof VehicleLoanSum,
    changeField?: keyof VehicleLoanSum,
): number {
    if (!sums) return 0;
    const changeKey = changeField ?? (`${String(field)}Change` as keyof VehicleLoanSum);
    const base = sums[field];
    const change = sums[changeKey];

    if (hasDefinedValue(base) && hasDefinedValue(change)) {
        return toNumber(change);
    }
    if (hasDefinedValue(base) && !hasDefinedValue(change)) {
        return toNumber(base);
    }
    if (hasDefinedValue(change)) {
        return toNumber(change);
    }
    return toNumber(base);
}

/** YOA Balance = effective Current + Non-Current (Bank Summary Card). */
export function resolveVehicleLoanCalculatedYearEndBalance(sums: Partial<VehicleLoanSum> | undefined): number {
    return resolveBalanceYoa(sums);
}

export function resolveVehicleLoanYearEndBalance(sums: Partial<VehicleLoanSum> | undefined): number {
    return resolveVehicleLoanCalculatedYearEndBalance(sums);
}

/** lastYoa Balance = Change ?? calc; fallback currentLast + stored nonCurrentLast. */
export function resolveVehicleLoanYearEndBalanceLast(sums: Partial<VehicleLoanSum> | undefined): number {
    if (!sums) return 0;
    const resolved = resolveBankLoanLastYoaTotal(sums, 0);
    if (resolved !== 0) return resolved;
    return toNumber(sums.currentLastYoa) + toNumber(sums.nonCurrentLastYoa);
}

export function hasCurrentYearSumValues(sums: Partial<VehicleLoanSum> | undefined): boolean {
    if (!sums) return false;
    return (
        hasDefinedValue(sums.currentYoa)
        || hasDefinedValue(sums.nonCurrentYoa)
        || hasDefinedValue(sums.balanceYoa)
        || hasDefinedValue(sums.interestYoa)
        || hasDefinedValue(sums.currentYoaChange)
        || hasDefinedValue(sums.nonCurrentYoaChange)
        || hasDefinedValue(sums.interestYoaChange)
        || hasDefinedValue(sums.balanceLastYoaChange)
    );
}

export function vehicleLoanSumsHasTrackableValues(sums: Partial<VehicleLoanSum> | undefined): boolean {
    if (!sums) return false;
    return (
        toNumber(sums.currentYoa) !== 0
        || toNumber(sums.nonCurrentYoa) !== 0
        || toNumber(sums.balanceYoa) !== 0
        || toNumber(sums.interestYoa) !== 0
        || toNumber(sums.currentLastYoa) !== 0
        || toNumber(sums.nonCurrentLastYoa) !== 0
        || toNumber(sums.balanceLastYoa) !== 0
        || toNumber(sums.interestLastYoa) !== 0
        || hasDefinedValue(sums.currentYoaChange)
        || hasDefinedValue(sums.nonCurrentYoaChange)
        || hasDefinedValue(sums.interestYoaChange)
        || hasDefinedValue(sums.balanceLastYoaChange)
        || hasDefinedValue(sums.currentLastYoaChange)
        || hasDefinedValue(sums.interestLastYoaChange)
        || sums.prevFinalBalance !== undefined
        || sums.currFinalBalance !== undefined
        || !!sums.editedInstalments
    );
}

/** xxxChange 字段：保存时必须显式传 null，避免 JSON 省略 undefined 导致后端不更新 */
export const VEHICLE_LOAN_SUM_CHANGE_FIELDS = [
    'currentYoaChange',
    'currentLastYoaChange',
    'nonCurrentYoaChange',
    'interestYoaChange',
    'interestLastYoaChange',
    'balanceLastYoaChange',
] as const satisfies ReadonlyArray<keyof VehicleLoanSum>;

function normalizeChangeFieldForSave(v: unknown): number | null {
    return hasDefinedValue(v) ? toNumber(v) : null;
}

/**
 * Build sums payload for save/API request.
 * 保留计算器得到的 xxx 原值；手动修改仅体现在 xxxChange 字段，不覆盖 xxx。
 */
export function buildVehicleLoanSumsForSave(sums: Partial<VehicleLoanSum> | undefined): Partial<VehicleLoanSum> {
    if (!sums) {
        return Object.fromEntries(
            VEHICLE_LOAN_SUM_CHANGE_FIELDS.map((field) => [field, null]),
        ) as Partial<VehicleLoanSum>;
    }
    const normalizeBase = (v: unknown): number | undefined => (hasDefinedValue(v) ? toNumber(v) : undefined);
    const lastYoaTotal = resolveVehicleLoanYearEndBalanceLast(sums);
    return {
        ...sums,
        currentYoa: normalizeBase(sums.currentYoa),
        nonCurrentYoa: normalizeBase(sums.nonCurrentYoa),
        balanceYoa: resolveBalanceYoa(sums),
        interestYoa: normalizeBase(sums.interestYoa),
        currentLastYoa: normalizeBase(sums.currentLastYoa),
        nonCurrentLastYoa: resolveNonCurrentLastYoa(sums, lastYoaTotal),
        balanceLastYoa: normalizeBase(sums.balanceLastYoa) ?? lastYoaTotal,
        interestLastYoa: normalizeBase(sums.interestLastYoa),
        currentYoaChange: normalizeChangeFieldForSave(sums.currentYoaChange),
        currentLastYoaChange: normalizeChangeFieldForSave(sums.currentLastYoaChange),
        nonCurrentYoaChange: normalizeChangeFieldForSave(sums.nonCurrentYoaChange),
        interestYoaChange: normalizeChangeFieldForSave(sums.interestYoaChange),
        interestLastYoaChange: normalizeChangeFieldForSave(sums.interestLastYoaChange),
        balanceLastYoaChange: normalizeChangeFieldForSave(sums.balanceLastYoaChange),
    };
}

function buildVehicleLoanMapKey(loan: { ppeId?: number; mv?: string }): string | null {
    if (loan.ppeId != null) return `ppe:${loan.ppeId}`;
    if (loan.mv) return `mv:${loan.mv}`;
    return null;
}

/** Merge local vehicle loan rows with hook/backend data for total aggregation. */
export function mergeVehicleLoansForTotals(
    localLoans: Array<Partial<VehicleLoanRow>>,
    ppeDataList: Array<{ ppeId?: number; mv: string }>,
    hookLoans?: VehicleLoanRow[],
): Array<Partial<VehicleLoanRow>> {
    const hookMap = new Map<string, VehicleLoanRow>();
    (hookLoans || []).forEach((loan) => {
        const key = buildVehicleLoanMapKey(loan);
        if (key) hookMap.set(key, loan);
    });

    return localLoans.map((loan, index) => {
        const ppe = ppeDataList[index];
        const key = ppe ? buildVehicleLoanMapKey(ppe) : buildVehicleLoanMapKey(loan);
        const hookLoan = key ? hookMap.get(key) : undefined;

        const localSums = loan.sums;
        const hookSums = hookLoan?.sums;

        const mergedLoan: Partial<VehicleLoanRow> = {
            ...hookLoan,
            ...loan,
            ...(ppe?.ppeId != null ? { ppeId: ppe.ppeId } : {}),
        };

        if (localSums && hasCurrentYearSumValues(localSums)) {
            mergedLoan.sums = localSums;
        } else if (hookSums) {
            mergedLoan.sums = hookSums;
        } else if (localSums) {
            mergedLoan.sums = localSums;
        }

        return mergedLoan;
    });
}

/** Aggregate current-year Total card values from all vehicle loan rows. */
export function aggregateCurrentYearTotalCardTotals(
    loans: Array<{ sums?: Partial<VehicleLoanSum>; totalInterest?: number; hpAmt?: number }>,
    deletedIndices: Set<number>,
) {
    const totals = {
        current: 0,
        nonCurrent: 0,
        balanceYoa: 0,
        interestYoa: 0,
    };

    loans.forEach((loan, index) => {
        if (deletedIndices.has(index)) return;
        const sums = loan.sums;
        if (!sums) return;

        totals.current += resolveVehicleLoanDisplayValue(sums, 'currentYoa', 'currentYoaChange');
        totals.nonCurrent += resolveVehicleLoanDisplayValue(sums, 'nonCurrentYoa', 'nonCurrentYoaChange');
        totals.balanceYoa += resolveBalanceYoa(sums);
        totals.interestYoa += effectiveLoanSum(sums.interestYoa, sums.interestYoaChange);
    });

    return totals;
}

/** Aggregate all vehicle loan sum fields (includes last year). */
export function aggregateVehicleLoanTotals(
    loans: Array<{ sums?: Partial<VehicleLoanSum>; totalInterest?: number; hpAmt?: number }>,
    deletedIndices: Set<number>,
) {
    const totals = {
        current: 0,
        nonCurrent: 0,
        balanceYoa: 0,
        interestYoa: 0,
        currentLastYoa: 0,
        nonCurrentLastYoa: 0,
        balanceLastYoa: 0,
        interestLastYoa: 0,
        totalInterest: 0,
        hpAmt: 0,
    };

    loans.forEach((loan, index) => {
        if (deletedIndices.has(index)) return;
        const sums = loan.sums;
        if (!sums) return;

        const lastTotal = resolveVehicleLoanYearEndBalanceLast(sums);
        totals.current += resolveVehicleLoanDisplayValue(sums, 'currentYoa', 'currentYoaChange');
        totals.nonCurrent += resolveVehicleLoanDisplayValue(sums, 'nonCurrentYoa', 'nonCurrentYoaChange');
        totals.balanceYoa += resolveBalanceYoa(sums);
        totals.interestYoa += effectiveLoanSum(sums.interestYoa, sums.interestYoaChange);
        totals.currentLastYoa += resolveVehicleLoanDisplayValue(sums, 'currentLastYoa', 'currentLastYoaChange');
        totals.nonCurrentLastYoa += resolveNonCurrentLastYoa(sums, lastTotal);
        totals.balanceLastYoa += lastTotal;
        totals.interestLastYoa += resolveVehicleLoanDisplayValue(sums, 'interestLastYoa', 'interestLastYoaChange');
        totals.totalInterest += toNumber(loan.totalInterest);
        totals.hpAmt += toNumber(loan.hpAmt);
    });

    return totals;
}
